import { useMemo } from 'react'
import { LineChart } from 'src/components/analytics/charts/LineChart'
import { NoData } from 'src/components/analytics/common/NoData'
import { ChartSkeleton } from 'src/components/overview/charts/common/Skeleton'
import { useDiscountData, UseDiscountOptions } from './useDiscountData'

export default function ConversionRateComparisonChart({
  forKey,
  isPercentage
}: UseDiscountOptions & { isPercentage: boolean }) {
  const { ref, isLoading, isError, discountData, selectedTimeScale } = useDiscountData({ forKey })

  const withDiscount = useMemo(() => {
    switch (forKey) {
      case 'conversion':
        return `${discountData.conversionRateComparison.withDiscount.toFixed(2)}%`
      case 'average_order_value':
        return discountData.averageOrderValueComparison.withDiscount.toLocaleString('en-IN', {
          maximumFractionDigits: 2,
          style: 'currency',
          currency: 'INR'
        })
      case 'completed_count':
        return discountData.orderPlaceComparison.withDiscount.toLocaleString('en-IN', { maximumFractionDigits: 2 })
      default:
        return 'NA'
    }
  }, [forKey, discountData])

  const withoutDiscount = useMemo(() => {
    switch (forKey) {
      case 'conversion':
        return `${discountData.conversionRateComparison.withoutDiscount.toFixed(2)}%`
      case 'average_order_value':
        return discountData.averageOrderValueComparison.withoutDiscount.toLocaleString('en-IN', {
          maximumFractionDigits: 2,
          style: 'currency',
          currency: 'INR'
        })
      case 'completed_count':
        return discountData.orderPlaceComparison.withoutDiscount.toLocaleString('en-IN', { maximumFractionDigits: 2 })
      default:
        return 'NA'
    }
  }, [forKey, discountData])

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (discountData.series.length > 0) {
      return (
        <>
          <div className='flex items-center gap-2 my-2 w-1/2 justify-between'>
            <div className='text-[18px] font-bold text-carbon mb-1 flex flex-col'>
              <span>{withDiscount}</span>
              <span className='text-xs text-carbon font-normal text-nowrap'>with discounts</span>
            </div>
            <span className='text-carbon rounded-full border-1 border-carbon-light px-2 py-1 text-xs h-fit w-fit'>
              vs
            </span>
            <div className='text-[18px] font-bold text-carbon mb-1 flex flex-col'>
              <span>{withoutDiscount}</span>
              <span className='text-xs text-carbon font-normal text-nowrap'>without discounts</span>
            </div>
          </div>
          <div className='h-[85%] w-full'>
            <LineChart series={discountData.series} isPercentage={isPercentage} selectedTimeScale={selectedTimeScale} />
          </div>
        </>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full'>
      {renderComponent()}
    </div>
  )
}
